import React, { Suspense, lazy } from 'react';
import {
  ThemeProvider,
  CssBaseline,
  Box,
  CircularProgress,
} from '@mui/material';
import {
  HashRouter as Router,
  Routes,
  Route,
  Navigate 
} from 'react-router-dom';
import AuthWrapper from './components/AuthWrapper';

import theme from './theme';
import Header from './components/Header';
import AppShell from './components/AppShell';
import PageErrorBoundary from './components/PageErrorBoundary';

// Eagerly loaded — navigation + common pages
import ProjectsPage from './pages/ProjectsPage';
import BrowserToolPage from './pages/BrowserToolPage';
import SettingsPage from './pages/SettingsPage';
import AgentOpsPage from './pages/AgentOpsPage';
const VirtualCoffeePage = lazy(() => import('./pages/VirtualCoffeePage'));

// Embedded apps (loaded when user navigates to them)
const OasisPage = lazy(() => import('./pages/OasisPage'));
const GKSPage = lazy(() => import('./pages/GKSPage'));
const DominoPage = lazy(() => import('./pages/DominoPage'));
const SignalPage = lazy(() => import('./pages/SignalPage'));
const SalesBenchPage = lazy(() => import('./pages/SalesBenchPage'));
const MirrorHubPage = lazy(() => import('./pages/MirrorHubPage'));
const DScanPage = lazy(() => import('./pages/DScanPage'));
const N8NPage = lazy(() => import('./pages/N8NPage'));
const HubPage = lazy(() => import('./pages/HubPage'));
const WPAPHPage = lazy(() => import('./pages/WPAPHPage'));
const WPLECPage = lazy(() => import('./pages/WPLECPage'));
const WPINIPage = lazy(() => import('./pages/WPINIPage'));
const WPTonishaPage = lazy(() => import('./pages/WPTonishaPage'));
const WPWebCreatorsPage = lazy(() => import('./pages/WPWebCreatorsPage'));
const AIExPage = lazy(() => import('./pages/AIExPage'));

// Lazy-loaded — data analysis pages (keep old layout)
const ProjectDashboardPage = lazy(() => import('./pages/ProjectDashboardPage'));
const DataInsightTool = lazy(() => import('./pages/DataInsightTool'));
const SimpleDataViewer = lazy(() => import('./pages/SimpleDataViewer'));
const ChartsPage = lazy(() => import('./pages/ChartsPage'));
const QualitativeOutputPage = lazy(() => import('./pages/QualitativeOutputPage'));
const UploadPage = lazy(() => import('./pages/UploadPage'));
const HelpPage = lazy(() => import('./pages/HelpPage'));
const CodaBrowserPage = lazy(() => import('./pages/CodaBrowserPage'));
const CampaignPage = lazy(() => import('./pages/CampaignPage'));
const GoogleSheetsPage = lazy(() => import('./pages/GoogleSheetsPage'));
const ProductsPage = lazy(() => import('./pages/ProductsPage'));
const DataProjectsPage = lazy(() => import('./pages/DataProjectsPage'));
const EcosystemTableView = lazy(() => import('./pages/EcosystemTableView'));
const CRMView = lazy(() => import('./components/CRMView'));
const FormsView = lazy(() => import('./components/FormsView'));

const NotFoundPage = () => <Box sx={{ p: 3 }}>Page Not Found</Box>;

const PageLoader = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
    <CircularProgress />
  </Box>
);

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthWrapper>
        <Router>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Routes with NEW unified layout (AppShell + Sidebar) */}
              <Route path="/" element={<AppShell><ProjectsPage /></AppShell>} />
              <Route path="/projects" element={<AppShell><ProjectsPage /></AppShell>} />
              <Route path="/agent-ops" element={<AppShell><PageErrorBoundary><AgentOpsPage /></PageErrorBoundary></AppShell>} />
              <Route path="/virtual-coffee" element={<AppShell><PageErrorBoundary><VirtualCoffeePage /></PageErrorBoundary></AppShell>} />
              <Route path="/oasis" element={<AppShell><OasisPage /></AppShell>} />
              <Route path="/gks" element={<AppShell><GKSPage /></AppShell>} />
              <Route path="/domino" element={<AppShell><DominoPage /></AppShell>} />
              <Route path="/mirror" element={<AppShell><MirrorHubPage /></AppShell>} />
              <Route path="/dscan" element={<AppShell><DScanPage /></AppShell>} />
              <Route path="/signal" element={<AppShell><SignalPage /></AppShell>} />
              <Route path="/sales-bench" element={<AppShell><SalesBenchPage /></AppShell>} />
              <Route path="/n8n" element={<AppShell><N8NPage /></AppShell>} />
              <Route path="/browser" element={<AppShell><BrowserToolPage /></AppShell>} />
              <Route path="/hub" element={<AppShell><HubPage /></AppShell>} />
              <Route path="/settings" element={<AppShell><SettingsPage /></AppShell>} />
              <Route path="/wp/aph" element={<AppShell><WPAPHPage /></AppShell>} />
              <Route path="/wp/lec" element={<AppShell><WPLECPage /></AppShell>} />
              <Route path="/wp/ini" element={<AppShell><WPINIPage /></AppShell>} />
              <Route path="/wp/tonisha" element={<AppShell><WPTonishaPage /></AppShell>} />
              <Route path="/wp/webcreat" element={<AppShell><WPWebCreatorsPage /></AppShell>} />
              <Route path="/aiex" element={<AppShell><AIExPage /></AppShell>} />

              {/* Routes with OLD layout (Header) — data analysis pages */}
              <Route path="/data-projects" element={
                <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: 'background.default' }}>
                  <Header />
                  <Box component="main" sx={{ flexGrow: 1 }}><DataProjectsPage /></Box>
                </Box>
              } />
              <Route path="/projects/:projectId" element={
                <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: 'background.default' }}>
                  <Header />
                  <Box component="main" sx={{ flexGrow: 1 }}><ProjectDashboardPage /></Box>
                </Box>
              } />
              <Route path="/projects/:projectId/data-insight/:fileId" element={
                <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: 'background.default' }}>
                  <Header />
                  <Box component="main" sx={{ flexGrow: 1 }}><DataInsightTool /></Box>
                </Box>
              } />
              <Route path="/projects/:projectId/simple-view/:fileId" element={
                <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: 'background.default' }}>
                  <Header />
                  <Box component="main" sx={{ flexGrow: 1 }}><SimpleDataViewer /></Box>
                </Box>
              } />
              <Route path="/projects/:projectId/charts" element={
                <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: 'background.default' }}>
                  <Header />
                  <Box component="main" sx={{ flexGrow: 1 }}><ChartsPage /></Box>
                </Box>
              } />
              <Route path="/projects/:projectId/qualitative" element={
                <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: 'background.default' }}>
                  <Header />
                  <Box component="main" sx={{ flexGrow: 1 }}><QualitativeOutputPage /></Box>
                </Box>
              } />
              <Route path="/coda" element={
                <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: 'background.default' }}>
                  <Header />
                  <Box component="main" sx={{ flexGrow: 1 }}><CodaBrowserPage /></Box>
                </Box>
              } />
              <Route path="/sheets" element={
                <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: 'background.default' }}>
                  <Header />
                  <Box component="main" sx={{ flexGrow: 1 }}><GoogleSheetsPage /></Box>
                </Box>
              } />
              <Route path="/campaign" element={
                <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: 'background.default' }}>
                  <Header />
                  <Box component="main" sx={{ flexGrow: 1 }}><CampaignPage /></Box>
                </Box>
              } />
              <Route path="/crm" element={
                <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: 'background.default' }}>
                  <Header />
                  <Box component="main" sx={{ flexGrow: 1 }}><CRMView /></Box>
                </Box>
              } />
              <Route path="/forms" element={
                <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: 'background.default' }}>
                  <Header />
                  <Box component="main" sx={{ flexGrow: 1 }}><FormsView /></Box>
                </Box>
              } />
              <Route path="/products" element={
                <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: 'background.default' }}>
                  <Header />
                  <Box component="main" sx={{ flexGrow: 1 }}><ProductsPage /></Box>
                </Box>
              } />
              <Route path="/ecosystem/:projectName/table/:tableName" element={
                <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: 'background.default' }}>
                  <Header />
                  <Box component="main" sx={{ flexGrow: 1 }}><EcosystemTableView /></Box>
                </Box>
              } />
              <Route path="/upload" element={
                <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: 'background.default' }}>
                  <Header />
                  <Box component="main" sx={{ flexGrow: 1 }}><UploadPage /></Box>
                </Box>
              } />
              <Route path="/docs" element={
                <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: 'background.default' }}>
                  <Header />
                  <Box component="main" sx={{ flexGrow: 1 }}><HelpPage /></Box>
                </Box>
              } />

              {/* 404 */}
              <Route path="/404" element={<Box sx={{ p: 3 }}>Page Not Found</Box>} />
              <Route path="*" element={<Navigate to="/404" replace />} />
            </Routes>
          </Suspense>
        </Router>
      </AuthWrapper>
    </ThemeProvider>
  );
}

export default App;
